import { useCallback, useEffect, useMemo, useRef } from 'react';

import { env } from '@/env';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveGames, setSelectedGames } from '@/store/core.slice';
import { useGamesQuery } from '@/services/endpoints/games';
import { CHESS_GAME, COC_GAME } from '@/lib/gameFeatures';
import { storage, StorageKey } from '@/lib/storage';
import type { Game } from '@/types';

/**
 * Game-selection state manager. Mirrors mobile-app's single-game picker
 * (`selectedGames === activeGames === [id]`) and persists to localStorage so
 * the choice survives reloads.
 *
 * Rehydrates from storage on first mount, then keeps storage in sync as the
 * user picks a different game.
 */
export function useGameSelection() {
  const dispatch = useAppDispatch();
  const selectedGames = useAppSelector((s) => s.core.selectedGames);
  const activeGames = useAppSelector((s) => s.core.activeGames);
  const { data: backendGames = [], isLoading } = useGamesQuery();
  // QChess / QClans aren't Laravel games — inject synthetic "Chess" / "Clash of
  // Clans" entries into the picker (and into activeGame resolution) when their
  // feature flags are on, so they're selectable like Clash Royale / eFootball.
  const games = useMemo(() => {
    const extra: Game[] = [];
    if (env.featureChessEnabled) extra.push(CHESS_GAME);
    if (env.featureCocEnabled) extra.push(COC_GAME);
    return extra.length > 0 ? [...backendGames, ...extra] : backendGames;
  }, [backendGames]);

  const rehydrated = useRef(false);
  useEffect(() => {
    if (rehydrated.current) return;
    rehydrated.current = true;
    const stored = storage.get(StorageKey.SelectedGameId);
    if (!stored) return;
    const id = Number(stored);
    if (!Number.isFinite(id) || id <= 0) return;
    dispatch(setSelectedGames([id]));
    dispatch(setActiveGames([id]));
  }, [dispatch]);

  const activeGameId = activeGames[0] ?? null;
  const activeGame: Game | null = useMemo(() => {
    if (!activeGameId) return null;
    return games.find((g) => g.id === activeGameId) ?? null;
  }, [activeGameId, games]);

  const selectGame = useCallback(
    (id: number) => {
      dispatch(setSelectedGames([id]));
      dispatch(setActiveGames([id]));
      storage.set(StorageKey.SelectedGameId, String(id));
    },
    [dispatch],
  );

  const clearGame = useCallback(() => {
    dispatch(setSelectedGames([]));
    dispatch(setActiveGames([]));
    storage.remove(StorageKey.SelectedGameId);
  }, [dispatch]);

  return {
    games,
    isLoading,
    activeGame,
    activeGameId,
    selectedGames,
    activeGames,
    selectGame,
    clearGame,
  };
}
